import { Component, Input, OnChanges } from '@angular/core';
import {
  BankAccountPaymentDetail,
  CargoPaymentAccount,
  CargoPaymentGroup,
  CargoPaymentPreviewResponse
} from 'src/app/core/interfaces/bank-account-payment-detail';
import {
  CARGO_PAYMENT_PREVIEW_SEVERITY,
  CargoPaymentPreviewSeverity,
  CargoPaymentPreviewStatusDict,
  CargoPaymentPreviewStatusEnum
} from 'src/app/core/enums/cargoPaymentPreviewStatus.enum';

/**
 * Un beneficiario con todos sus servicios: los que se pagan y los que no.
 * Los grupos que emiten transferencia vienen de `groups`; el resto se arma en
 * el front para que ningún servicio quede suelto.
 */
interface PaymentPreviewGroupView {
  key: string;
  bankAccount: BankAccountPaymentDetail;
  services: CargoPaymentAccount[];
  /** false cuando ningún servicio del beneficiario es pagable. */
  transfers: boolean;
  totalAmount: number;
  estimated: boolean;
  payableCount: number;
  excludedCount: number;
  /** Servicios en negativo que explican por qué el total es aproximado. */
  negativeCargos: CargoPaymentAccount[];
  expanded: boolean;
}

/**
 * Muestra el preview de un pago antes de generarlo: a quién se le transfiere,
 * cuánto, y qué servicios seleccionados quedan fuera del archivo y por qué.
 */
@Component({
  selector: 'app-payment-preview',
  templateUrl: './payment-preview.component.html',
  styleUrls: ['./payment-preview.component.scss']
})
export class PaymentPreviewComponent implements OnChanges {
  /** Respuesta del preview. `null` cuando el back respondió 204. */
  @Input() preview: CargoPaymentPreviewResponse;
  /** Ids enviados, en el orden en que el usuario los seleccionó. */
  @Input() cargoIds: string[] = [];
  @Input() loading = false;
  /** Mensaje a mostrar cuando no fue posible consultar el preview. */
  @Input() error: string;

  groupViews: PaymentPreviewGroupView[] = [];
  /** Servicios a los que el back no les pudo resolver una cuenta bancaria. */
  unresolvedServices: CargoPaymentAccount[] = [];
  unresolvedExpanded = true;
  private cargoRows: CargoPaymentAccount[] = [];

  ngOnChanges(): void {
    this.buildViews();
  }

  private buildViews(): void {
    const accounts = this.preview && this.preview.accounts ? this.preview.accounts : {};
    const groups = this.preview && this.preview.groups ? this.preview.groups : [];
    const expandedByKey = this.groupViews.reduce((acc, groupView) => {
      acc[groupView.key] = groupView.expanded;
      return acc;
    }, {} as { [key: string]: boolean });

    // Se recorren los ids enviados para no ocultarle al usuario los servicios
    // que seleccionó y no se van a pagar.
    this.cargoRows = (this.cargoIds || [])
      .map(cargoId => accounts[cargoId])
      .filter(account => !!account);

    const views = groups.map(group => this.buildGroupFromBackend(group, accounts));
    this.unresolvedServices = [];
    this.attachExcludedServices(views);
    this.finishGroups(views, expandedByKey);
    this.groupViews = views;
  }

  /** Un grupo tal como lo devolvió el back: es una línea del archivo bancario. */
  private buildGroupFromBackend(
    group: CargoPaymentGroup,
    accounts: { [cargoId: string]: CargoPaymentAccount }
  ): PaymentPreviewGroupView {
    const services = (group.cargoIds || [])
      .map(cargoId => accounts[cargoId])
      .filter(account => !!account);

    return {
      key: group.groupKey || this.accountKey(group.bankAccount),
      bankAccount: group.bankAccount,
      services,
      transfers: true,
      totalAmount: group.totalAmount,
      estimated: !!group.estimated,
      payableCount: 0,
      excludedCount: 0,
      negativeCargos: [],
      expanded: false
    };
  }

  /**
   * Cuelga cada servicio no pagable del beneficiario que le corresponde.
   *
   * El back solo manda `groupKey` para los pagables, así que el emparejamiento
   * se hace comparando los campos de la cuenta contra los del grupo — la misma
   * llave con la que agrupa el back, sin parsear su `groupKey`, que es opaco.
   * Si no coincide con ninguna línea bancaria, el beneficiario se arma igual
   * pero marcado como que no genera transferencia.
   */
  private attachExcludedServices(views: PaymentPreviewGroupView[]): void {
    const placed = views.reduce((acc, view) => {
      view.services.forEach(service => {
        if (service.cargoId) {
          acc[service.cargoId] = true;
        }
      });
      return acc;
    }, {} as { [cargoId: string]: boolean });

    this.cargoRows.forEach(service => {
      if (service.cargoId && placed[service.cargoId]) {
        return;
      }

      const key = this.accountKey(service.bankAccount);
      if (!key) {
        this.unresolvedServices.push(service);
        return;
      }

      let view = views.filter(candidate => candidate.key === key)[0];
      if (!view) {
        view = {
          key,
          bankAccount: service.bankAccount,
          services: [],
          transfers: false,
          totalAmount: null,
          estimated: false,
          payableCount: 0,
          excludedCount: 0,
          negativeCargos: [],
          expanded: false
        };
        views.push(view);
      }
      view.services.push(service);
    });
  }

  /** Conteos, orden de servicios y estado inicial de cada beneficiario. */
  private finishGroups(views: PaymentPreviewGroupView[], expandedByKey: { [key: string]: boolean }): void {
    const order = (this.cargoIds || []).reduce((acc, cargoId, index) => {
      acc[cargoId] = index;
      return acc;
    }, {} as { [cargoId: string]: number });

    const negativeCargos = this.cargoRows.filter(
      account => account.status === CargoPaymentPreviewStatusEnum.NEGATIVE_BALANCE && !!account.driverDocument
    );

    views.forEach(view => {
      view.services.sort((a, b) => {
        const first = order[a.cargoId] === undefined ? Number.MAX_SAFE_INTEGER : order[a.cargoId];
        const second = order[b.cargoId] === undefined ? Number.MAX_SAFE_INTEGER : order[b.cargoId];
        return first - second;
      });

      view.payableCount = view.services.filter(service => this.isPayable(service)).length;
      view.excludedCount = view.services.length - view.payableCount;

      // El cruce de saldos negativos lo hace el back por conductor, no por
      // cuenta, así que el servicio que lo causa puede estar en otro grupo.
      const driverDocuments = view.services
        .map(service => service.driverDocument)
        .filter(document => !!document);
      view.negativeCargos = view.estimated
        ? negativeCargos.filter(cargo => driverDocuments.indexOf(cargo.driverDocument) >= 0)
        : [];

      // Lo que exige atención no puede quedar escondido tras un clic.
      view.expanded = expandedByKey[view.key] !== undefined
        ? expandedByKey[view.key]
        : (view.excludedCount > 0 || view.estimated || !view.transfers);
    });
  }

  /**
   * Identidad del beneficiario, con la misma forma que la llave del archivo
   * bancario: tipo y número de documento, banco, cuenta y tipo de cuenta.
   * Devuelve null cuando no hay cuenta con la cual emparejar.
   */
  private accountKey(bankAccount: BankAccountPaymentDetail): string {
    if (!bankAccount || !bankAccount.accountNumber) {
      return null;
    }
    return [
      bankAccount.documentTypeId,
      bankAccount.document,
      bankAccount.bank && bankAccount.bank.code,
      bankAccount.accountNumber,
      bankAccount.accountTypeCode
    ].map(value => value === null || value === undefined ? '' : String(value)).join('|');
  }

  toggleGroup(groupView: PaymentPreviewGroupView): void {
    groupView.expanded = !groupView.expanded;
  }

  toggleUnresolved(): void {
    this.unresolvedExpanded = !this.unresolvedExpanded;
  }

  get hasPreview(): boolean {
    return !!this.preview && !!this.cargoRows.length;
  }

  get hasTransfers(): boolean {
    return this.groupViews.some(groupView => groupView.transfers);
  }

  /** La compañía no consolida: cada servicio sale en su propia transferencia. */
  get isNotConsolidated(): boolean {
    return !!this.preview && this.preview.consolidated === false;
  }

  get hasEstimatedGroups(): boolean {
    return !!this.preview && !!this.preview.estimated;
  }

  get totalServices(): number {
    return this.cargoRows.length;
  }

  /** Servicios seleccionados que no van a generar pago. */
  get totalExcluded(): number {
    return this.cargoRows.filter(service => !this.isPayable(service)).length;
  }

  severity(account: CargoPaymentAccount): CargoPaymentPreviewSeverity {
    const status = account && account.status ? account.status : '';
    return CARGO_PAYMENT_PREVIEW_SEVERITY[status] || 'warning';
  }

  statusMessage(account: CargoPaymentAccount): string {
    if (!account) {
      return '';
    }
    if (account.message) {
      return account.message;
    }
    return CargoPaymentPreviewStatusDict[account.status] || account.status || '';
  }

  isPayable(account: CargoPaymentAccount): boolean {
    return !!account && account.status === CargoPaymentPreviewStatusEnum.OK;
  }

  bankName(bankAccount: BankAccountPaymentDetail): string {
    return bankAccount && bankAccount.bank && bankAccount.bank.name ? bankAccount.bank.name : '';
  }

  beneficiaryName(bankAccount: BankAccountPaymentDetail): string {
    return bankAccount && bankAccount.name ? bankAccount.name : 'Beneficiario sin nombre';
  }
}
